import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { PamGrid } from '@1823-partners/core';

const metadataTitle = value => value.split('_').map(word =>
  ['id', 'cusip', 'bob', 'adco', 'intex'].includes(word) ? word.toUpperCase()
    : word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
const displayValue = value => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};
const validCount = value => Number.isInteger(value) && value >= 0;

// Encode paths to distinguish literal dots in keys from nested fields.
function flattenMetadata(value, path = [], result = new Map()) {
  if (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length) {
    Object.entries(value).forEach(([key, child]) => flattenMetadata(child, [...path, key], result));
  } else if (path.length) {
    result.set(JSON.stringify(path), { path, value });
  }
  return result;
}

export function parseMetadata(value) {
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch { return value; }
  }
  return value;
}

export function buildEventGridData(events, offset = 0) {
  const fields = new Map();
  const rows = events.map((event, index) => {
    const parsed = parseMetadata(event.META);
    const meta = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    const progress = ['chunks', 'books'].filter(group =>
      validCount(meta[`total_${group}`]) && validCount(meta[`processed_${group}`]))
      .map(group => `${meta[`processed_${group}`]} of ${meta[`total_${group}`]} ${group}` +
        (validCount(meta[`failed_${group}`]) ? ` · ${meta[`failed_${group}`]} failed` : ''))
      .join('; ');
    const row = {
      position: offset + index,
      eventTime: event.EVENT_TIME,
      status: event.STATUS || 'Unknown',
      stage: typeof meta.stage === 'string' ? metadataTitle(meta.stage) : 'Unavailable',
      progress: progress || '—',
    };
    flattenMetadata(parsed, parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? [] : ['metadata']).forEach(({ path, value }, key) => {
      // Stage already has a dedicated column; all other metadata is retained.
      if (path.length === 1 && path[0] === 'stage' && typeof value === 'string') return;
      const field = `meta:${key}`;
      fields.set(field, path.map(metadataTitle).join(' / '));
      row[field] = value;
    });
    return row;
  });
  return { rows, fields };
}

export default function ScenarioEventGrid({ events, offset, runId, formatTime }) {
  const { rows, fields } = useMemo(() => buildEventGridData(events, offset), [events, offset]);
  const columns = useMemo(() => [
    { field: 'eventTime', headerName: 'Time', valueFormatter: p => formatTime(p.value), maxWidth: undefined },
    { field: 'status', headerName: 'Status', maxWidth: undefined },
    { field: 'stage', headerName: 'Stage', maxWidth: undefined },
    { field: 'progress', headerName: 'Progress', maxWidth: undefined },
    ...Array.from(fields, ([field, headerName]) => ({
      field, headerName, wrapText: true, autoHeight: true,
      valueFormatter: p => displayValue(p.value),
      tooltipValueGetter: p => displayValue(p.value),
    })),
  ], [fields, formatTime]);
  return <Box role="region" aria-label={`Event history for ${runId}`}
    sx={{ mt: 2, width: '100%', maxWidth: 'calc(100vw - 96px)', minWidth: 0 }}>
    <PamGrid
      rowData={rows}
      columnDefs={columns}
      defaultColDef={{ flex: 0, minWidth: 70, maxWidth: 480, editable: false, resizable: true, sortable: true,
        filter: 'agTextColumnFilter', cellDataType: false }}
      getRowId={row => String(row.position)}
      height={440}
      showToolbar={false}
      showQuickFilter={false}
      showFooter={false}
      agGridProps={{ suppressFieldDotNotation: true, suppressColumnVirtualisation: true,
        autoSizeStrategy: { type: 'fitCellContents' } }}
    />
    <Box component="details" mt={1}>
      <summary>View raw event data (current page)</summary>
      <Box component="pre" sx={{ maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {JSON.stringify(events, null, 2)}
      </Box>
    </Box>
  </Box>;
}
